// T28 hold-M measure — press and hold `M`, drag on the map, see a transient line + distance label
// (haversine auto m/km on a real map; planar mode reports a unitless "planar units" distance);
// releasing clears it. No mode button. `M`-hold disables MapLibre's own `dragPan` for the duration
// (otherwise the map would pan itself under the drag) — always re-enabled on key-up/unmount.
// Keydown/keyup are native listeners on the SAME `containerRef` div `SpatialMap` hands MapLibre as
// its `container`; MapLibre's canvas (a focusable descendant) is what actually holds DOM focus, so
// this is focus-scoped for free (see `useGhostLayer.ts`'s header comment for the full rationale).

import { useEffect, useRef, useState, type RefObject } from 'react';
import type { Map as MlMap, MapMouseEvent } from 'maplibre-gl';
import { useAnalytics } from '../../../../core/analytics';
import type { PlanarTransform } from './spatialMapTypes';

const EARTH_RADIUS_METERS = 6371000;

/** Great-circle distance between two `[lng, lat]` points, in meters. */
export function haversineDistanceMeters([lng1, lat1]: [number, number], [lng2, lat2]: [number, number]): number {
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return EARTH_RADIUS_METERS * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

/** Plain Euclidean distance between two ORIGINAL-data-space `[x, y]` points (planar mode). */
export function euclideanDistance([x1, y1]: [number, number], [x2, y2]: [number, number]): number {
  return Math.hypot(x2 - x1, y2 - y1);
}

/** Auto m/km label from a meter distance. */
export function formatMeasureDistance(meters: number): string {
  if (!Number.isFinite(meters)) return '';
  return meters < 1000 ? `${Math.round(meters)} m` : `${(meters / 1000).toFixed(2)} km`;
}

/** Unitless planar-space label. */
export function formatPlanarDistance(units: number): string {
  if (!Number.isFinite(units)) return '';
  return `${units.toFixed(2)} planar units`;
}

export type MeasureLine = { start: { x: number; y: number }; current: { x: number; y: number }; label: string } | null;

// Module-scope — "first drag per session" means per app-open (this process), not per mount.
let measureTrackedThisSession = false;

export function useMeasureGesture(
  containerRef: RefObject<HTMLElement | null>,
  mapRef: RefObject<MlMap | null>,
  planarTransform?: PlanarTransform | null,
  disabled?: boolean,
): MeasureLine {
  const { track } = useAnalytics();
  const [line, setLine] = useState<MeasureLine>(null);
  const planarRef = useRef(planarTransform);
  planarRef.current = planarTransform;
  // Latest disabled flag read inside the once-registered keydown handler (polygon-draw owns the map).
  const disabledRef = useRef(disabled);
  disabledRef.current = disabled;

  useEffect(() => {
    const el = containerRef.current;
    const mapOrNull = mapRef.current;
    if (!el || !mapOrNull) return;
    const map: MlMap = mapOrNull;

    let mHeld = false;
    let dragging = false;
    let startLngLat: [number, number] | null = null;
    let startPoint: { x: number; y: number } | null = null;

    function labelFor(currentLngLat: [number, number]): string {
      if (!startLngLat) return '';
      const transform = planarRef.current;
      if (transform) {
        return formatPlanarDistance(euclideanDistance(transform.inverse(startLngLat), transform.inverse(currentLngLat)));
      }
      return formatMeasureDistance(haversineDistanceMeters(startLngLat, currentLngLat));
    }

    function onKeyDown(e: KeyboardEvent) {
      if (disabledRef.current || (e.key !== 'm' && e.key !== 'M') || e.ctrlKey || e.metaKey || mHeld) return;
      mHeld = true;
      map.dragPan.disable();
    }
    function onKeyUp(e: KeyboardEvent) {
      if (e.key !== 'm' && e.key !== 'M') return;
      mHeld = false;
      map.dragPan.enable();
      dragging = false;
      startLngLat = null;
      startPoint = null;
      setLine(null);
    }
    function onMouseDown(e: MapMouseEvent) {
      if (!mHeld) return;
      dragging = true;
      startLngLat = [e.lngLat.lng, e.lngLat.lat];
      startPoint = { x: e.point.x, y: e.point.y };
      setLine({ start: startPoint, current: startPoint, label: labelFor(startLngLat) });
    }
    function onMouseMove(e: MapMouseEvent) {
      if (!dragging || !startPoint) return;
      const current: [number, number] = [e.lngLat.lng, e.lngLat.lat];
      setLine({ start: startPoint, current: { x: e.point.x, y: e.point.y }, label: labelFor(current) });
    }
    function onMouseUp() {
      if (!dragging) return;
      dragging = false;
      startLngLat = null;
      startPoint = null;
      setLine(null);
      if (!measureTrackedThisSession) {
        measureTrackedThisSession = true;
        track('feature:spatial_measure');
      }
    }

    el.addEventListener('keydown', onKeyDown, { capture: true });
    el.addEventListener('keyup', onKeyUp, { capture: true });
    map.on('mousedown', onMouseDown);
    map.on('mousemove', onMouseMove);
    map.on('mouseup', onMouseUp);
    return () => {
      el.removeEventListener('keydown', onKeyDown, { capture: true });
      el.removeEventListener('keyup', onKeyUp, { capture: true });
      map.off('mousedown', onMouseDown);
      map.off('mousemove', onMouseMove);
      map.off('mouseup', onMouseUp);
      map.dragPan.enable();
    };
  }, [containerRef, mapRef, track]);

  return line;
}

/** Test-only reset of the module-scope "tracked this session" flag. */
export function __resetMeasureTrackedForTests(): void {
  measureTrackedThisSession = false;
}
